/**
 * Welcome Modal for First-Time Users
 * Shows onboarding flow for new registrations
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, Wallet, ShoppingBag, BookOpen, ArrowRight, CheckCircle } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose, username }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const onboardingSteps = [
    {
      title: 'Selamat Bergabung!',
      subtitle: `Halo ${username}, selamat datang di Canvango`,
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <p className="text-gray-600 leading-relaxed mb-6">
            Platform terpercaya untuk mendapatkan akun Business Manager dan Personal Facebook berkualitas tinggi dengan garansi resmi.
          </p>
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center justify-center space-x-2 text-blue-700">
              <FontAwesomeIcon icon={faGift} className="w-5 h-5" />
              <span className="font-semibold text-sm">Bonus member baru tersedia!</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Cara Kerja Platform',
      subtitle: 'Langkah mudah untuk memulai',
      content: (
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="bg-green-100 p-2 rounded-xl flex-shrink-0">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">1. Top Up Saldo</h4>
              <p className="text-gray-600 text-sm">Isi saldo untuk mulai berbelanja dengan aman</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 p-2 rounded-xl flex-shrink-0">
              <ShoppingBag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">2. Pilih Produk</h4>
              <p className="text-gray-600 text-sm">Jelajahi akun BM & Personal tersedia</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="bg-purple-100 p-2 rounded-xl flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">3. Dapatkan Akun</h4>
              <p className="text-gray-600 text-sm">Terima akun berkualitas dengan garansi</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Siap Memulai?',
      subtitle: 'Pilih langkah pertama Anda',
      content: (
        <div className="space-y-4">
          <Link
            to="/top-up"
            onClick={onClose}
            className="group w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl p-4 flex items-center justify-between transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <div className="flex items-center space-x-3">
              <Wallet className="w-6 h-6" />
              <div className="text-left">
                <div className="font-semibold">Top Up Saldo</div>
                <div className="text-green-100 text-sm">Mulai dengan mengisi saldo</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          
          <Link
            to="/akun-bm"
            onClick={onClose}
            className="group w-full bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-2xl p-4 flex items-center justify-between transition-all duration-300"
          >
            <div className="flex items-center space-x-3">
              <ShoppingBag className="w-6 h-6 text-gray-600" />
              <div className="text-left">
                <div className="font-semibold text-gray-900">Lihat Produk Dulu</div>
                <div className="text-gray-600 text-sm">Jelajahi akun tersedia</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          
          <Link
            to="/tutorial"
            onClick={onClose}
            className="group w-full bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-2xl p-3 flex items-center justify-between transition-all duration-300"
          >
            <div className="flex items-center space-x-3">
              <BookOpen className="w-5 h-5 text-gray-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900 text-sm">Baca Tutorial</div>
                <div className="text-gray-600 text-xs">Pelajari cara menggunakan platform</div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      ),
    },
  ];

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <h2 className="text-xl font-bold mb-1">{currentStepData.title}</h2>
            <p className="text-blue-100 text-sm">{currentStepData.subtitle}</p>
          </div>
          
          {/* Progress Indicator */}
          <div className="flex justify-center space-x-2 mt-4">
            {onboardingSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'bg-white w-6'
                    : index < currentStep
                    ? 'bg-white/80'
                    : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        {!isLastStep && (
          <div className="px-6 pb-6 flex justify-between">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
            >
              Lewati
            </button>
            
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl font-medium text-sm transition-colors flex items-center space-x-2"
            >
              <span>Lanjut</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Last Step Footer */}
        {isLastStep && (
          <div className="px-6 pb-6">
            <button
              onClick={onClose}
              className="w-full text-gray-500 hover:text-gray-700 font-medium text-sm py-2 transition-colors"
            >
              Nanti saja, ke Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};